import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { useSession } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  RiBookmarkLine,
  RiPlayLine,
  RiDeleteBinLine,
  RiDatabase2Line,
  RiLoader2Line,
  RiFileTextLine,
} from '@remixicon/react';
import { api, SavedQuery } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export function SavedQueriesPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [queries, setQueries] = useState<SavedQuery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState<SavedQuery | null>(null);

  useEffect(() => {
    if (session?.user?.id) {
      loadQueries();
    }
  }, [session?.user?.id]);

  const loadQueries = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoading(true);
      const result = await api.getSavedQueries(session.user.id);
      setQueries(result.queries);
    } catch (error: any) {
      toast.error('Failed to load saved queries');
    } finally {
      setIsLoading(false);
    }
  };

  const runQuery = (query: SavedQuery) => {
    navigate({ to: `/query/${query.databaseId}` });
  };

  const confirmDelete = (query: SavedQuery) => {
    setQueryToDelete(query);
    setDeleteDialogOpen(true);
  };

  const deleteQuery = async () => {
    if (!queryToDelete || !session?.user?.id) return;

    try {
      await api.deleteSavedQuery(queryToDelete.id, session.user.id);
      toast.success('Query deleted successfully');
      setQueries(queries.filter((q) => q.id !== queryToDelete.id));
      setDeleteDialogOpen(false);
      setQueryToDelete(null);
    } catch (error: any) {
      toast.error('Failed to delete query');
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 z-10">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4 mr-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Saved Queries</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ModeToggle />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto md:p-8 lg:p-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10">
                  <RiBookmarkLine className="size-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Saved Queries</h1>
                  <p className="text-muted-foreground">Manage your saved SQL queries</p>
                </div>
              </div>
            </motion.div>

            {/* Queries List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RiLoader2Line className="size-8 animate-spin text-primary" />
              </div>
            ) : queries.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <RiBookmarkLine className="size-16 mx-auto mb-4 text-muted-foreground/20" />
                <h3 className="text-lg font-semibold mb-2">No saved queries yet</h3>
                <p className="text-muted-foreground mb-6">
                  Save queries from the query editor to see them here
                </p>
                <Button onClick={() => navigate({ to: '/databases' })}>
                  <RiDatabase2Line className="size-4 mr-2" />
                  Go to Databases
                </Button>
              </motion.div>
            ) : (
              <ScrollArea className="h-[calc(100vh-16rem)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {queries.map((query, index) => (
                    <motion.div
                      key={query.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="group hover:border-primary/50 transition-all duration-300 hover:shadow-md h-full">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10 flex-shrink-0">
                                <RiFileTextLine className="size-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base truncate mb-1">
                                  {query.name}
                                </h3>
                                {query.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                    {query.description}
                                  </p>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  Saved {formatDistanceToNow(new Date(query.createdAt), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Query Preview */}
                          <code className="block text-xs font-mono bg-muted/50 px-3 py-2 rounded-md mb-3 truncate">
                            {query.query}
                          </code>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => runQuery(query)}
                              className="flex-1"
                            >
                              <RiPlayLine className="size-4 mr-2" />
                              Run Query
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => confirmDelete(query)}
                              className="text-destructive hover:text-destructive"
                            >
                              <RiDeleteBinLine className="size-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </main>
      </SidebarInset>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Query?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{queryToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteQuery}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
